// components/StaffDialog.tsx
import AsyncAutocompleteSingleSelectField from "@/components/form/components/AsyncAutocompleteSingleSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useStaffByIdQuery,
} from "@/hooks/api/staff.queries";
import {
  StaffParamsType,
  fetchDesignationOptions,
} from "@/services/staff.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAllDesignationsQuery } from "@/hooks/api/designation.queries";
import { fetchRoleOptions } from "@/services/role.service";
import { rolesKey } from "@/hooks/api/role.queries";
import { Spinner } from "@/components";

type StaffDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: StaffParamsType;
  staffId?: string;
  onCancel: () => void;
  onSuccess?: (staff: { id: string; name: string }) => void;
  prefillDesignation?: { id: string; name: string };
};

const formSchema = z.object({
  name: z.string().min(1, "Full Name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().regex(/^\d{10}$/, "Phone number must be exactly 10 digits"),
  designation_id: z.string().min(1, "Designation is required"),
  role_id: z.string().optional().or(z.literal("")),
});

type StaffFormType = z.infer<typeof formSchema>;

const StaffDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  staffId,
  onCancel,
  onSuccess,
  prefillDesignation,
}: StaffDialogProps) => {
  const createMutation = useCreateStaffMutation(filters);
  const updateMutation = useUpdateStaffMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { data: staffData, isLoading: isLoadingStaff } = useStaffByIdQuery(
    staffId || "",
    mode === "edit" && isOpen
  );

  const { data: designations = [] } = useAllDesignationsQuery();

  const form = useForm<StaffFormType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      designation_id: mode === "create" && prefillDesignation ? prefillDesignation.id : "",
      role_id: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && staffData) {
        setTimeout(() => {
          form.reset({
            name: staffData.name || "",
            email: staffData.email || "",
            phone: staffData.phone || "",
            designation_id: staffData.designation_id || "",
            role_id: staffData.role_id || "",
          });
        }, 0);
      } else {
        form.reset({
          name: "",
          email: "",
          phone: "",
          designation_id: mode === "create" && prefillDesignation ? prefillDesignation.id : "",
          role_id: "",
        });
      }
    }
  }, [mode, staffData, isOpen, form, prefillDesignation]);

  const handleSubmit = (values: StaffFormType) => {
    const selectedDesignation = designations.find(
      (d) => d.id === values.designation_id
    );

    const submissionData = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      designation: selectedDesignation?.name || "unknown",
      designation_id: values.designation_id,
      role_id: values.role_id,
    };

    if (mode === "create") {
      createMutation.mutate(submissionData as any, {
        onSuccess: (data: any) => {
          const createdStaff = data?.data || data;
          onSuccess?.({ id: createdStaff.id, name: createdStaff.name });
          onCancel();
        },
      });
    } else if (mode === "edit" && staffId) {
      const { email, ...updateData } = submissionData;
      updateMutation.mutate(
        { id: staffId, data: updateData as any },
        {
          onSuccess: () => onCancel(),
        }
      );
    }
  };

  const defaultDesignationOption = staffData?.designation_id
    ? {
        label: staffData.designation_name || "Designation",
        value: staffData.designation_id,
      }
    : mode === "create" && prefillDesignation
      ? {
          label: prefillDesignation.name,
          value: prefillDesignation.id,
        }
      : undefined;

  const defaultStaffRoleOption = staffData?.role_id
    ? [
        {
          label: staffData.role_name || "Role",
          value: staffData.role_id,
        },
      ]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Staff" : "Edit Staff Member"}
          </DialogTitle>
        </DialogHeader>

        {mode === "edit" && isLoadingStaff ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="grid grid-cols-1 gap-4 py-2"
            >
              <InputField<StaffFormType>
                name="name"
                label="Full Name"
                placeholder="e.g. Inbarasan K"
                type="text"
                disabled={isPending}
                required
              />

              <InputField<StaffFormType>
                name="email"
                label="Email"
                placeholder="e.g. user@example.com"
                type="email"
                disabled={isPending || mode === "edit"}
                required={mode === "create"}
              />

              <InputField<StaffFormType>
                name="phone"
                label="Phone Number"
                placeholder="e.g. 9876543210"
                type="tel"
                disabled={isPending}
                required
              />

              <AsyncAutocompleteSingleSelectField<StaffFormType>
                name="designation_id"
                label="Designation"
                placeholder="Select designation"
                fetchOptions={async (params) => {
                  const res = await fetchDesignationOptions({
                    search: params.search,
                    page: 1,
                    pageSize: params.limit,
                  } as any);
                  return res.items;
                }}
                queryKey={["designations"]}
                defaultOption={defaultDesignationOption}
                disabled={true}
                required
              />

              <AsyncSingleSelectField<StaffFormType>
                name="role_id"
                label="Role"
                placeholder="Select role (optional)"
                fetchOptions={fetchRoleOptions}
                queryKey={[...rolesKey.all]}
                defaultOptions={defaultStaffRoleOption}
                disabled={isPending}
              />

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending
                    ? mode === "create"
                      ? "Adding..."
                      : "Saving..."
                    : mode === "create"
                      ? "Add Staff"
                      : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StaffDialog;